// src/pages/StarRec/Blacklists/BlacklistBusinesses.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as blacklistApi from "../../../api/blacklist/blacklistApi";

function MessageBar({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="alert alert-info d-flex justify-content-between align-items-center">
      <div>{message}</div>
      <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white border rounded p-3"
        style={{ width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0">{title}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="mb-3">
      <label className="form-label fw-semibold">{label}</label>
      <input className="form-control" value={value ?? ""} readOnly />
    </div>
  );
}

function safeText(v) {
  return (v ?? "").toString().trim();
}

const BlacklistBusinesses = () => {
  const didLoadRef = useRef(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [filterCountry, setFilterCountry] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [message, setMessage] = useState("");
  const showMessage = (txt) => {
    setMessage(txt);
    window.clearTimeout(window.__msgTimer);
    window.__msgTimer = window.setTimeout(() => setMessage(""), 2000);
  };

  useEffect(() => {
    document.title = "StarRec Blacklisted Businesses";
  }, []);

  const normalizeBusiness = (b) => ({
    id: (b?._id || b?.id || "").toString(),
    businessName: b?.business_name || b?.businessName || b?.name || "",
    branchName: b?.branch_name || b?.branchName || "",
    country: b?.country || "",
    state: b?.state || "",
    city: b?.city || "",
    registrationNumber: b?.registration_number || b?.registrationNumber || "",
    categoryId: b?.category_id || b?.categoryId || "",
    blacklistReason: b?.blacklist_reason || b?.blacklistReason || "",
    raw: b,
  });

  const loadBlacklisted = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await blacklistApi.getBlacklisted();
      const businesses = Array.isArray(res?.businesses) ? res.businesses : [];
      setRows(businesses.map(normalizeBusiness));
    } catch (e) {
      setError(e?.message || "Failed to load blacklisted businesses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;
    loadBlacklisted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countries = useMemo(() => {
    const set = new Set(rows.map((r) => safeText(r.country)).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  const states = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .map((r) => r.state)
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry]);

  const cities = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .filter((r) => (filterState ? r.state === filterState : true))
      .map((r) => r.city)
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry, filterState]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterCountry && r.country !== filterCountry) return false;
      if (filterState && r.state !== filterState) return false;
      if (filterCity && r.city !== filterCity) return false;
      return true;
    });
  }, [rows, filterCountry, filterState, filterCity]);

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId]
  );

  const openView = (row) => {
    setSelectedId(row.id);
    setViewOpen(true);
  };

  const closeView = () => {
    setViewOpen(false);
    setSelectedId(null);
  };

  const unblacklist = async (businessId) => {
    setBusy(true);
    try {
      await blacklistApi.unblacklistBusiness(businessId);
      showMessage("Un-blacklisted successfully");
      closeView();
      await loadBlacklisted();
    } catch (e) {
      alert(e?.message || "Un-blacklist failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="fs-4 fw-bold m-0">Blacklisted Businesses</h1>
        <button className="btn btn-outline-primary" onClick={loadBlacklisted} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <MessageBar message={message} onClose={() => setMessage("")} />

      {error ? (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <div>{error}</div>
          <button className="btn btn-sm btn-outline-danger" onClick={loadBlacklisted}>
            Retry
          </button>
        </div>
      ) : null}

      {/* Filters */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <label className="form-label">Country</label>
          <select
            className="form-select"
            value={filterCountry}
            onChange={(e) => {
              setFilterCountry(e.target.value);
              setFilterState("");
              setFilterCity("");
            }}
          >
            <option value="">All</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            className="form-select"
            value={filterState}
            onChange={(e) => {
              setFilterState(e.target.value);
              setFilterCity("");
            }}
            disabled={!filterCountry}
          >
            <option value="">All</option>
            {states.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">City</label>
          <select
            className="form-select"
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            disabled={!filterState}
          >
            <option value="">All</option>
            {cities.map((ct) => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead>
            <tr>
              <th>Business Name</th>
              <th>Branch</th>
              <th>City</th>
              <th>State</th>
              <th>Country</th>
              <th style={{ width: 120 }} className="text-center">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">
                  No blacklisted businesses found.
                </td>
              </tr>
            ) : (
              filteredRows.map((r) => (
                <tr key={r.id} className="table-danger">
                  <td>{r.businessName || "-"}</td>
                  <td>{r.branchName || "-"}</td>
                  <td>{r.city || "-"}</td>
                  <td>{r.state || "-"}</td>
                  <td>{r.country || "-"}</td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(r)}>
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      <Modal open={viewOpen} onClose={closeView} title="View Blacklisted Business">
        {!selected ? (
          <div className="text-muted">No data.</div>
        ) : (
          <>
            <div className="row">
              <div className="col-md-6">
                <Field label="Business Name" value={selected.businessName} />
              </div>
              <div className="col-md-6">
                <Field label="Branch Name" value={selected.branchName} />
              </div>

              <div className="col-md-4">
                <Field label="Country" value={selected.country} />
              </div>
              <div className="col-md-4">
                <Field label="State" value={selected.state} />
              </div>
              <div className="col-md-4">
                <Field label="City" value={selected.city} />
              </div>

              <div className="col-md-6">
                <Field label="Registration Number" value={selected.registrationNumber} />
              </div>
              <div className="col-md-6">
                <Field label="Category ID" value={selected.categoryId} />
              </div>

              <div className="col-12">
                <Field label="Blacklist Reason" value={selected.blacklistReason} />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-outline-secondary" onClick={closeView} disabled={busy}>
                Back
              </button>
              <button
                className="btn btn-outline-success"
                onClick={() => unblacklist(selected.id)}
                disabled={busy}
              >
                {busy ? "..." : "Un-Blacklist"}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default BlacklistBusinesses;
